import { createContext, useContext, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react'
import { invariant } from '../lib/invariant'
import equal from 'fast-deep-equal'

const RouterContext = createContext<[string, Dispatch<SetStateAction<string>>] | null>(null)

export function RouterProvider({ children }: { children: ReactNode }) {
  const [pathname, setPathname] = useState('')
  return <RouterContext.Provider value={[pathname, setPathname]}>{children}</RouterContext.Provider>
}

export function useRouter() {
  const state = useContext(RouterContext)
  invariant(state, 'useRouter() must be called from a child component of <RouterProvider />.')
  const [pathname, setPathname] = state
  const push = (pathname: string) => setPathname(pathname)
  const back = () => setPathname(pathname.split('/').slice(0, -1).join('/'))
  return {
    pathname,
    push,
    back
  }
}

const ParamsContext = createContext<Record<string, string>>({})

export function useParams() {
  return useContext(ParamsContext)
}

export function Route({ pathname, children }: { pathname: string; children?: ReactNode }) {
  const { pathname: current } = useRouter()
  const currentSegments = current.split('/')
  const pathnameSegments = pathname.split('/')
  if (currentSegments.length < pathnameSegments.length) {
    return null
  }
  const params: Record<string, string> = {}
  for (let i = 0; i < currentSegments.length; i++) {
    if (i >= pathnameSegments.length) {
      break
    }
    if (pathnameSegments[i].startsWith('{') && pathnameSegments[i].endsWith('}')) {
      params[pathnameSegments[i].slice(1, -1)] = decodeURIComponent(currentSegments[i])
      continue
    }
    if (pathnameSegments[i] !== currentSegments[i]) {
      return null
    }
  }
  return <ParamsContext.Provider value={params}>{children}</ParamsContext.Provider>
}
